import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[25]

export const KWP_25_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-25-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-25',
  title: 'Instalación fotovoltaica de 25 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 25 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para grandes edificios residenciales.',
  keywords: [
    'instalación fotovoltaica 25 kWp precio',
    'placas solares 25 kWp coste',
    'cuánto cuesta una instalación de 25 kWp',
    '25 kWp instalación solar edificio',
  ],
  eyebrow: 'Tamaño de instalación: 25 kWp',
  h1: 'Instalación fotovoltaica de 25 kWp: precio y valores orientativos',
  intro:
    'Una instalación de 25 kWp es adecuada para grandes edificios residenciales con varias ' +
    'viviendas o un consumo total muy elevado - y se sitúa justo en el límite a partir del cual, ' +
    'en Alemania, cambia el sistema de retribución EEG en 2026. Aquí encontrará valores ' +
    'orientativos sobre superficie de tejado y número de paneles, un desglose de costes y justo ' +
    'debajo una calculadora con el precio medio para 25 kWp ya calculado a partir de presupuestos ' +
    'reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 25 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 25 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 25 kWp suele encajar con un edificio residencial grande con varias ' +
        'viviendas o un inmueble con un consumo total muy elevado, consumo anual total a menudo ' +
        'superior a 16.000 kWh. Con varias viviendas en el mismo edificio, la empresa instaladora ' +
        'aclara además cuestiones sobre el reparto del consumo.',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 25 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'A este tamaño, las empresas instaladoras suelen repartir los paneles entre varias ' +
        'vertientes o secciones de tejado - un cálculo exacto para su tejado lo ofrece la ' +
        'calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: con 25 kWp normalmente varias cadenas e inversores',
        'Montaje/mano de obra: fijación en el tejado, cableado sobre varias secciones',
        'Andamiaje: más extenso cuanto mayor es la superficie de tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, posibles varios contadores con varias viviendas',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        'En edificios con varias viviendas conviene aclarar de antemano si tiene más sentido un ' +
        'modelo de autoconsumo compartido entre inquilinos o un suministro propio del edificio - ' +
        'esto influye tanto en el diseño técnico como en la estructura de costes.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        'En Alemania, 2026 es el último año con retribución fija garantizada (EEG) para nuevas ' +
        'instalaciones de menos de 25 kWp - quien ponga en marcha una instalación de 25 kWp en ' +
        '2026 ya entra en el sistema de retribución modificado a partir de ese límite. Hasta ' +
        '30 kWp, los ingresos de instalaciones fotovoltaicas siguen completamente exentos de ' +
        'impuesto sobre la renta, sin necesidad de darse de alta como autónomo. Un cálculo exacto ' +
        'para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Qué significa el límite de 25 kWp en la retribución EEG de 2026?',
      a: 'En Alemania, 2026 es el último año con retribución fija garantizada (EEG) para nuevas instalaciones de menos de 25 kWp - para instalaciones a partir de 25 kWp ya se aplica el sistema modificado.',
    },
    {
      q: '¿Necesito darme de alta como autónomo con 25 kWp?',
      a: 'No - en Alemania, los ingresos de instalaciones fotovoltaicas de hasta 30 kWp están completamente exentos de impuesto sobre la renta, sin alta de actividad económica ni declaración de ingresos y gastos. 25 kWp está dentro de ese límite.',
    },
    {
      q: '¿Para qué edificios es adecuada una instalación de 25 kWp?',
      a: 'Típicamente para grandes edificios residenciales con varias viviendas o inmuebles con un consumo total muy elevado. Con varias viviendas, la empresa instaladora aclara además cuestiones sobre el reparto del consumo o un posible modelo de autoconsumo compartido.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 20 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-20-kwp-precio' },
    { label: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-15-kwp-precio' },
    { label: 'Instalación eléctrica para fotovoltaica', href: '/instalacion-electrica-fotovoltaica' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
